import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import FollowUpController from '../controllers/FollowUpController';
import commonHandler from '../errorHandlers/common-handler';
import { StoreState } from '../store-interfaces';
import CrudActionContainer from './CrudActionContainer';
import {
  FollowUpKind,
  FollowUpModel,
  QqFollowUpModel,
} from '../model/FollowUpModel';

class FollowUpCRUDActions extends CrudActionContainer {
  retrievedByKindAction: string;
  resetAction: string;

  constructor() {
    super('FOLLOW_UP');

    this.resetAction = super.makeActionKey('RESET');
    this.retrievedByKindAction = super.makeActionKey('RETRIEVED_BY_KIND');
  }

  retrieveAll(botId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));
      try {
        const response = await FollowUpController.getAll(botId);
        const { followUps } = response;
        dispatch(this.retrievedAll(followUps));
        return followUps;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieveByKind(botId: number, kind: FollowUpKind) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));
      try {
        const response = await FollowUpController.getByKind(botId, kind);
        const { followUps } = response;
        dispatch(this.retrievedByKindActionCreator(followUps, kind));
        return followUps;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  update(
    botId: number,
    id: number,
    entity: Partial<FollowUpModel<QqFollowUpModel>>,
    kind: FollowUpKind,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));
      try {
        const response = await FollowUpController.putFollowUp(
          botId,
          id,
          entity,
          kind,
        );
        const { followUp } = response;
        dispatch(this.updateLocal(followUp));
        return followUp;
      } catch (e) {
        return commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrievedByKindActionCreator(
    entities: FollowUpModel<QqFollowUpModel>[],
    kind: FollowUpKind,
  ) {
    return {
      type: this.retrievedByKindAction,
      entities,
      kind,
    };
  }

  reset() {
    return {
      type: this.resetAction,
    };
  }
}

const followUpCRUDActions = new FollowUpCRUDActions();

export default followUpCRUDActions;

export const useFollowUpCRUDActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  return useMemo(
    () => ({
      reset: () => dispatch(followUpCRUDActions.reset()),
      retrieveAll: (botId: number) =>
        dispatch(followUpCRUDActions.retrieveAll(botId)),
      retrieveByKind: (botId: number, kind: FollowUpKind) =>
        dispatch(followUpCRUDActions.retrieveByKind(botId, kind)),
      update: (
        botId: number,
        id: number,
        entity: Partial<FollowUpModel<QqFollowUpModel>>,
        kind: FollowUpKind,
      ) => dispatch(followUpCRUDActions.update(botId, id, entity, kind)),
    }),
    [dispatch],
  );
};
